import React, { useEffect, useState } from "react";
import FilterListIcon from "@mui/icons-material/FilterList";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import { Box, Button, Stack, Typography } from "@mui/material";
import { isEqual } from "lodash";
import type { FilterState, FilterValue } from "hooks/useFilters";
import SidebarLayout, { SidebarSide } from "../SidebarLayout";
import type { FilterSectionFieldProps } from "./FilterSection";
import FilterSection from "./FilterSection";

export type FilterLayoutProps = React.PropsWithChildren<{
  filterSections: FilterSectionFieldProps[];
  headerFilters?: FilterSectionFieldProps[];
  defaultFilter: FilterState;
  filter: FilterState;
  onFilterChange: (key: string, value: FilterValue) => void;
  onResetFilter: () => void;
}>;

function FilterLayout(props: FilterLayoutProps) {
  const { filterSections, defaultFilter, filter, onFilterChange, onResetFilter, children, headerFilters } = props;
  const [isShowingFilter, setIsShowingFilter] = useState<boolean>(() =>
    filterSections.some((section) => !!filter[section.filterKey])
  );

  // A section can gain a value after the initial mount is complete, e.g. if gated behind a
  // feature flag check. This is a defensive check to ensure that scenario still works.
  useEffect(() => {
    if (filterSections.some((section) => !!filter[section.filterKey])) {
      setIsShowingFilter(true);
    }
  }, [filter, filterSections]);

  const sideBarContent = () => {
    return (
      <>
        <Stack
          spacing={2}
          direction={"row"}
          sx={{
            justifyContent: "space-between",
            my: 2,
          }}
        >
          <Typography variant="subtitle1">Filters</Typography>
          <Button disabled={isEqual(filter, defaultFilter)} onClick={onResetFilter}>
            Reset
          </Button>
        </Stack>
        <Stack
          spacing={2}
          sx={{
            justifyContent: "center",
          }}
        >
          {filterSections.map((section, index) => (
            <Box key={index}>
              <FilterSection setFilter={onFilterChange} value={filter[section.filterKey]} {...section} />
            </Box>
          ))}
        </Stack>
      </>
    );
  };

  return (
    <>
      <Stack
        spacing={2}
        direction={"row"}
        sx={{
          alignItems: "flex-end",
          my: 2,
        }}
      >
        {headerFilters &&
          headerFilters.map((section, index) => (
            <Box key={index}>
              <FilterSection setFilter={onFilterChange} value={filter[section.filterKey]} {...section} />
            </Box>
          ))}
        {filterSections.length > 0 && (
          <Box>
            <Button onClick={() => setIsShowingFilter(!isShowingFilter)} size="small">
              {isShowingFilter && <FilterListOffIcon />}
              {!isShowingFilter && <FilterListIcon />}
              <span style={{ marginLeft: "0.25rem" }}>{`${!isShowingFilter ? "Show" : "Hide"} advanced filters`}</span>
            </Button>
          </Box>
        )}
      </Stack>
      <SidebarLayout sideBar={sideBarContent()} side={SidebarSide.Left} showSidebarContent={isShowingFilter}>
        {children}
      </SidebarLayout>
    </>
  );
}

export default FilterLayout;
